import { reverse, whereEq } from 'ramda';
import type { Location, NodeEntry, PathRef, Point } from 'slate';
import { Editor, Element, Node, Path, Range, Text } from 'slate';

import type { CapitalizationStyle } from './capitalization';
import {
  applyCapitalization,
  capitalizationParts,
  UNCHANGED_CAPITALIZATION_MESSAGE,
} from './capitalization';
import type {
  AlineSelection,
  AlineText,
  DeleteEdit,
  FontSizeFormat,
  InsertEdit,
  ToggleableKeys,
  VerticalAlign,
} from '../internal';
import {
  AlineTransforms,
  defaultFontSize,
  EditingMode,
  findFont,
  isAlineInlineElement,
  isDeleteEdit,
  isInsertEdit,
  isParagraphElement,
  Queries,
  SelEventSource,
} from '../internal';
import { CAPITALIZATION_VOID_SEPARATOR } from '../queries/capitalization';

function normalizeFontSize(fontSizeValue: number): number {
  const roundedDown = Math.floor(fontSizeValue);

  const decimal = fontSizeValue - roundedDown;

  if (fontSizeValue < 1) return 1;
  if (fontSizeValue > 400) return 400;
  if ((fontSizeValue >= 1 || fontSizeValue <= 400) && decimal < 0.5) return roundedDown;
  return roundedDown + 0.5;
}

function getCurrentFontSize(editor: Editor): number {
  if (editor.selection?.listMarker)
    return Queries.activeListMarkerAtLocation(editor)?.fontSize.size ?? defaultFontSize;

  const [textEntry] = Editor.nodes<AlineText>(editor, { match: Text.isText, voids: true });
  return textEntry ? Queries.activeFontSizePt(textEntry[0]) : defaultFontSize;
}

function applyFontSizeChange(
  editor: Editor,
  options: {
    authorId: string;
    changeFontSize: (existingFontSize: number) => number;
    editingMode: EditingMode;
    voids?: boolean;
  }
): void {
  AlineTransforms.withoutNormalizing(editor, () => {
    const { authorId, changeFontSize, editingMode, voids = true } = options;
    if (!editor.selection) return;
    if (Range.isCollapsed(editor.selection)) {
      /**
       * If editor.marks is carrying cursor styles, we must use those to increment/decrement.  Otherwise use the relevant text node.
       */
      if (!editor.marks?.fontSize?.size) {
        const newFontSize: FontSizeFormat = {
          origin: 'USER_APPLIED',
          size: changeFontSize(getCurrentFontSize(editor)),
        };

        AlineTransforms.changeFontSize(editor, { authorId, editingMode, newFontSize, voids });
      } else {
        const changedMarkFontSize: FontSizeFormat = {
          origin: 'USER_APPLIED',
          size: changeFontSize(editor.marks.fontSize.size),
        };

        AlineTransforms.changeFontSize(editor, {
          authorId,
          editingMode,
          newFontSize: changedMarkFontSize,
          voids,
        });
      }

      return;
    }

    /**
     * With an active grid (table cell rectangle) selection, resize each selected cell's text nodes
     * individually (Queries.nodes iterates per cell). Whole cells are covered, so no edge splitting
     * is needed.
     */
    if (Queries.getIsGridSelection(editor)) {
      for (const [textNode, textPath] of Queries.nodes<AlineText>(editor, {
        match: Text.isText,
        voids,
      })) {
        const newFontSize: FontSizeFormat = {
          origin: 'USER_APPLIED',
          size: changeFontSize(Queries.activeFontSizePt(textNode)),
        };

        AlineTransforms.changeFontSize(editor, {
          at: textPath,
          authorId,
          editingMode,
          newFontSize,
          voids,
        });
      }

      return;
    }

    const rangeAfterSplit = AlineTransforms.splitTextAtEdges(editor, { editingMode });

    if (!rangeAfterSplit) return;

    const textNodeEntries = Editor.nodes<AlineText>(editor, {
      at: rangeAfterSplit,
      match: Text.isText,
      voids,
    });

    for (const textNodeEntry of textNodeEntries) {
      const [textNode, textPath] = textNodeEntry;

      const fontSizePt = Queries.activeFontSizePt(textNode);

      const newFontSize: FontSizeFormat = {
        origin: 'USER_APPLIED',
        size: changeFontSize(fontSizePt),
      };

      AlineTransforms.changeFontSize(editor, {
        at: textPath,
        authorId,
        editingMode,
        newFontSize,
        voids,
      });
    }

    // We set the selection to the rangeAfterSplit since, under the hood when doing multiple splits at different points, Slate doesn't set the selection to where we want.
    AlineTransforms.select(editor, rangeAfterSplit);
  });
}

const FormatTransforms = {
  applyCapitalizationStyle: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      style: CapitalizationStyle;
    }
  ): string | undefined => {
    return AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, style } = options;
      // Editing mode applies the case directly, so it replaces a pending case change on the same text
      if (editingMode === EditingMode.EDITING) rejectCaseChangeSuggestions(editor, { authorId });

      const ranges = Queries.capitalizationRanges(editor);
      const plans = ranges.map(range => capitalizationReplacements(editor, range, style));
      const replacements = plans.flatMap(plan => plan.replacements);
      const unaligned = plans.some(plan => plan.unaligned);
      if (!replacements.length) return unaligned ? UNCHANGED_CAPITALIZATION_MESSAGE : undefined;

      // A grid selection is re-selected as itself, so it keeps covering the same cell rectangle.
      const isGridSelection = Queries.getIsGridSelection(editor);
      const at = isGridSelection ? editor.selection : ranges.at(0);
      const gridColumns: Pick<
        AlineSelection,
        'gridSelectionAnchorColumn' | 'gridSelectionFocusColumn'
      > = {
        gridSelectionAnchorColumn: isGridSelection
          ? editor.selection?.gridSelectionAnchorColumn
          : null,
        gridSelectionFocusColumn: isGridSelection
          ? editor.selection?.gridSelectionFocusColumn
          : null,
      };
      const insertedRefs: PathRef[] = [];

      try {
        const rangeRef = at ? Editor.rangeRef(editor, at, { affinity: 'outward' }) : null;

        for (const { at: replacementAt, text } of reverse(replacements)) {
          // A case change keeps its text node, so a pending suggestion or comment stays on the text
          if (editingMode === EditingMode.EDITING) {
            replaceTextInPlace(editor, { at: replacementAt, text });
            continue;
          }

          const endRef = Editor.pointRef(editor, Range.end(replacementAt), { affinity: 'forward' });
          try {
            AlineTransforms.insertText(editor, {
              at: replacementAt,
              authorId,
              editingMode,
              select: false,
              textToInsert: text,
            });
            const end = endRef.current;
            if (editingMode === EditingMode.SUGGESTING && end) {
              const insertedAt = Editor.after(editor, end);
              const candidates = insertedAt ? [end.path, insertedAt.path] : [end.path];
              for (const path of candidates) {
                const node = Node.get(editor, path);
                if (
                  Text.isText(node) &&
                  node.text === text &&
                  node.suggestedEdits?.some(
                    edit => isInsertEdit(edit) && edit.authorId === authorId
                  )
                ) {
                  insertedRefs.push(Editor.pathRef(editor, [...path]));
                  break;
                }
              }
            }
          } finally {
            endRef.unref();
          }
        }

        const nextRange = rangeRef?.unref();
        if (!nextRange) return unaligned ? UNCHANGED_CAPITALIZATION_MESSAGE : undefined;

        const isForward = Range.isForward(nextRange);
        let { anchor, focus } = nextRange;
        // The suggested text lands after the struck text, so the end of the range grows over it
        if (editingMode === EditingMode.SUGGESTING) {
          if (isForward) focus = extendOverInsertedText(editor, { insertedRefs, point: focus });
          else anchor = extendOverInsertedText(editor, { insertedRefs, point: anchor });
        }

        // Stay in the paragraph, so a selection in a table cell never reaches the previous cell
        if (!isGridSelection && isForward && focus.offset === 0 && Path.hasPrevious(focus.path))
          focus = Editor.end(editor, Path.previous(focus.path));
        AlineTransforms.setSelection(editor, {
          eventSource: SelEventSource.PROGRAMMATIC,
          props: { anchor, focus, ...gridColumns },
        });
      } finally {
        for (const ref of insertedRefs) {
          ref.unref();
        }
      }

      if (unaligned) return UNCHANGED_CAPITALIZATION_MESSAGE;
    });
  },
  changeFontSize: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      newFontSize: FontSizeFormat;
      at?: Location;
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, newFontSize, voids = true } = options;
      if (!at) return;

      newFontSize.size = normalizeFontSize(newFontSize.size);

      AlineTransforms.setTextProps(editor, {
        at,
        authorId,
        editingMode,
        textProps: { fontSize: newFontSize },
        voids,
      });
    });
  },
  /**
   * Decrement font size by a unit.
   */
  decrementFontSize: (
    editor: Editor,
    options: { authorId: string; editingMode: EditingMode; unit?: number; voids?: boolean }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, unit = 1, voids = true } = options;

      applyFontSizeChange(editor, {
        authorId,
        changeFontSize: (existingFontSize: number) => existingFontSize - unit,
        editingMode,
        voids,
      });
    });
  },
  /**
   * Increment font size by a unit.
   */
  incrementFontSize: (
    editor: Editor,
    options: { authorId: string; editingMode: EditingMode; unit?: number; voids?: boolean }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, unit = 1, voids = true } = options;

      applyFontSizeChange(editor, {
        authorId,
        changeFontSize: (existingFontSize: number) => existingFontSize + unit,
        editingMode,
        voids,
      });
    });
  },
  setFontType: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      type: AlineText['fontType']['type'];
      at?: Location;
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, type, voids = true } = options;
      if (!at) return;

      const {
        fontDetails: { fontFamily },
      } = findFont(type);
      const newFontType: Pick<AlineText, 'fontType'> = {
        fontType: { fontFamily, origin: 'USER_APPLIED', type },
      };

      AlineTransforms.setTextProps(editor, {
        at,
        authorId,
        editingMode,
        textProps: newFontType,
        voids,
      });
    });
  },

  /**
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-396.
   */
  toggleTextFormatting: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      formatKey: ToggleableKeys;
      at?: Location;
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      let { at = editor.selection } = options;
      const { authorId, editingMode, formatKey, voids = true } = options;
      if (!at) return;

      /**
       * A grid (table cell rectangle) selection is not unhung: `at` keeps referring to the live
       * selection, so isTextFormatActive samples and setTextProps applies per selected cell rather
       * than over the linear range (which would also cover cells outside the rectangle).
       */
      const isGridSelection = !!Queries.getGridSelectionInfoForLocation(editor, { at });

      if (!isGridSelection && Range.isRange(at)) {
        const unhungRange = AlineTransforms.unhangRange(editor, { at });
        if (!unhungRange) return;

        at = unhungRange;
      }

      const formatActiveOnAllTextNodes = Queries.isTextFormatActive(editor, formatKey, {
        at,
        listMarker: true,
        voids,
      });
      const newFormatValue: AlineText[ToggleableKeys] = formatActiveOnAllTextNodes
        ? { active: false, origin: 'USER_APPLIED' }
        : { active: true, origin: 'USER_APPLIED' };

      AlineTransforms.setTextProps(editor, {
        at,
        authorId,
        editingMode,
        textProps: { [formatKey]: newFormatValue },
        voids,
      });
    });
  },
  toggleVerticalAlign: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      verticalAlign: VerticalAlign;
      at?: Location;
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      let { at = editor.selection } = options;
      const { authorId, editingMode, verticalAlign, voids = true } = options;
      if (!at) return;

      // A grid selection is not unhung so sampling and applying stay per selected cell (see
      // toggleTextFormatting for details).
      const isGridSelection = !!Queries.getGridSelectionInfoForLocation(editor, { at });

      if (!isGridSelection && Range.isRange(at)) {
        const unhungRange = AlineTransforms.unhangRange(editor, { at });
        if (!unhungRange) return;

        at = unhungRange;
      }

      const formatActiveOnAllTextNodes = Queries.isTextFormatActive(editor, 'verticalAlign', {
        at,
        voids,
      });

      const formats = Queries.formats(editor, { at, voids });
      const isSameFormat = (Array.isArray(formats) ? formats : [formats]).some(format => {
        const renderedFormat = format && Queries.renderedFormat('verticalAlign', format);
        return Boolean(renderedFormat) && whereEq({ align: verticalAlign }, renderedFormat);
      });

      const newFormatValue: AlineText['verticalAlign'] =
        formatActiveOnAllTextNodes && isSameFormat
          ? {
              align: 'baseline',
              origin: 'USER_APPLIED',
            }
          : {
              align: verticalAlign,
              origin: 'USER_APPLIED',
            };

      AlineTransforms.setTextProps(editor, {
        at,
        authorId,
        editingMode,
        textProps: { verticalAlign: newFormatValue },
        voids,
      });
    });
  },
};

export default FormatTransforms;

/** Reject each pending case change the selection fully covers, so the new case applies to the original text. */
function rejectCaseChangeSuggestions(editor: Editor, options: { authorId: string }): void {
  const { authorId } = options;

  for (const range of Queries.capitalizationRanges(editor)) {
    for (const entry of Editor.nodes(editor, { at: range, match: Text.isText })) {
      const [, insertedPath] = entry;
      const caseChange = findCaseChangeEdits(editor, entry);
      if (!caseChange || !Range.surrounds(range, Editor.range(editor, insertedPath))) continue;

      const deletedPath = Path.previous(insertedPath);
      selectStruckText(editor, { deletedPath, insertedPath });
      AlineTransforms.rejectSuggestedDeletion(editor, {
        deleteEditEntry: { edit: caseChange.deleteEdit, range: Editor.range(editor, deletedPath) },
        editingMode: EditingMode.EDITING,
      });
      AlineTransforms.rejectSuggestedInsertion(editor, {
        authorId,
        editingMode: EditingMode.EDITING,
        insertEditEntry: { edit: caseChange.insertEdit, range: Editor.range(editor, insertedPath) },
      });
    }
  }
}

/** Find a suggested deletion directly followed by an insertion that differs from it only in case. */
function findCaseChangeEdits(
  editor: Editor,
  entry: NodeEntry<Text>
): { deleteEdit: DeleteEdit; insertEdit: InsertEdit } | null {
  const [inserted, insertedPath] = entry;
  if (!Path.hasPrevious(insertedPath)) return null;

  const deleted = Node.get(editor, Path.previous(insertedPath));
  if (!Text.isText(deleted)) return null;

  const insertEdit = inserted.suggestedEdits?.find(isInsertEdit);
  if (!insertEdit || inserted.suggestedEdits?.some(isDeleteEdit)) return null;

  const deleteEdit = deleted.suggestedEdits?.find(isDeleteEdit);
  if (!deleteEdit || deleted.suggestedEdits?.some(isInsertEdit)) return null;

  const isSameLetters =
    applyCapitalization(inserted.text, 'uppercase') ===
    applyCapitalization(deleted.text, 'uppercase');
  return isSameLetters ? { deleteEdit, insertEdit } : null;
}

/** Move the selection edges on a case change onto its struck text, before the suggested text is emptied. */
function selectStruckText(
  editor: Editor,
  options: { deletedPath: Path; insertedPath: Path }
): void {
  const { deletedPath, insertedPath } = options;
  const { selection } = editor;
  if (!selection) return;

  const [start, end] = Range.edges(selection);
  const isStartOnCaseChange =
    Path.equals(start.path, deletedPath) || Path.equals(start.path, insertedPath);
  const struckStart = isStartOnCaseChange ? Editor.start(editor, deletedPath) : start;
  const struckEnd = Path.equals(end.path, insertedPath) ? Editor.end(editor, deletedPath) : end;
  const [anchor, focus] = Range.isForward(selection)
    ? [struckStart, struckEnd]
    : [struckEnd, struckStart];

  AlineTransforms.setSelection(editor, {
    eventSource: SelEventSource.PROGRAMMATIC,
    props: { anchor, focus },
  });
}

/** Replace the text of a range inside one text node, unlike insertText, which may split it off. */
function replaceTextInPlace(editor: Editor, options: { at: Range; text: string }): void {
  const { at, text } = options;
  const [start, end] = Range.edges(at);
  const { offset, path } = start;
  const removedText = Node.leaf(editor, path).text.slice(offset, end.offset);

  editor.apply({ offset, path, text: removedText, type: 'remove_text' });
  editor.apply({ offset, path, text, type: 'insert_text' });
}

/** Move a point at the end of a struck text to the end of the suggested text right after it. */
function extendOverInsertedText(
  editor: Editor,
  options: { insertedRefs: PathRef[]; point: Point }
): Point {
  const { insertedRefs, point } = options;
  if (!Editor.isEnd(editor, point, point.path)) return point;

  const after = Editor.after(editor, point);
  if (!after) return point;

  const isInserted = insertedRefs.some(ref => ref.current && Path.equals(ref.current, after.path));
  return isInserted ? Editor.end(editor, after.path) : point;
}

function capitalizationReplacements(
  editor: Editor,
  at: Range,
  style: CapitalizationStyle
): { replacements: { at: Range; text: string }[]; unaligned: boolean } {
  const paragraphs = [...Editor.nodes(editor, { at, match: isParagraphElement })].map(
    ([, path]) => {
      const paragraphRange = Range.intersection(at, Editor.range(editor, path));
      if (!paragraphRange || Range.isCollapsed(paragraphRange)) return null;
      return collectCapitalizationRuns(editor, paragraphRange);
    }
  );
  const sources = paragraphs.map(paragraph => (paragraph ? capitalizationSource(paragraph) : ''));
  const combined = sources.join('\n');
  const transformed = applyCapitalization(combined, style);
  if (transformed === combined) return { replacements: [], unaligned: false };

  const parts = transformed.split('\n');
  const paragraphTexts =
    parts.length === paragraphs.length
      ? parts
      : sources.map(source => applyCapitalization(source, style));

  let unaligned = false;
  const replacements = paragraphs.flatMap((paragraph, index) => {
    const paragraphText = paragraphTexts[index];
    if (!paragraph || paragraphText === undefined) return [];
    const plan = replacementsForRuns(paragraph, paragraphText, style);
    if (plan.unaligned) unaligned = true;
    return plan.replacements;
  });
  return { replacements, unaligned };
}

interface CapitalizationRun {
  readonly range: Range;
  readonly text: string;
  readonly voidsBefore: number;
}

interface CapitalizationParagraph {
  readonly runs: readonly CapitalizationRun[];
  readonly trailingVoids: number;
}

function collectCapitalizationRuns(editor: Editor, paragraphRange: Range): CapitalizationParagraph {
  const runs: CapitalizationRun[] = [];
  let pendingVoids = 0;

  for (const [node, path] of Editor.nodes(editor, { at: paragraphRange, voids: true })) {
    if (Element.isElement(node) && Editor.isVoid(editor, node) && isAlineInlineElement(node)) {
      pendingVoids += 1;
    } else if (Text.isText(node) && !node.suggestedEdits?.some(isDeleteEdit)) {
      const inVoid = Editor.above(editor, {
        at: path,
        match: ancestor => Element.isElement(ancestor) && Editor.isVoid(editor, ancestor),
        voids: true,
      });
      const runRange = inVoid
        ? null
        : Range.intersection(paragraphRange, Editor.range(editor, path));
      if (runRange && !Range.isCollapsed(runRange)) {
        const [start, end] = Range.edges(runRange);
        runs.push({
          range: runRange,
          text: node.text.slice(start.offset, end.offset),
          voidsBefore: pendingVoids,
        });
        pendingVoids = 0;
      }
    }
  }

  return { runs, trailingVoids: pendingVoids };
}

function capitalizationSource(paragraph: CapitalizationParagraph): string {
  let text = '';
  for (const run of paragraph.runs) {
    text += CAPITALIZATION_VOID_SEPARATOR.repeat(run.voidsBefore) + run.text;
  }

  return text + CAPITALIZATION_VOID_SEPARATOR.repeat(paragraph.trailingVoids);
}

function replacementsForRuns(
  paragraph: CapitalizationParagraph,
  paragraphText: string,
  style: CapitalizationStyle
): { replacements: { at: Range; text: string }[]; unaligned: boolean } {
  const original = capitalizationSource(paragraph);
  if (paragraphText === original) return { replacements: [], unaligned: false };

  const parts = capitalizationParts(original, style);
  if (parts && parts.join('') === paragraphText) {
    let index = 0;
    const replacements = paragraph.runs.flatMap(run => {
      index += run.voidsBefore * [...CAPITALIZATION_VOID_SEPARATOR].length;
      const count = [...run.text].length;
      const text = parts.slice(index, index + count).join('');
      index += count;
      if (text === run.text) return [];
      return [{ at: run.range, text }];
    });
    return { replacements, unaligned: false };
  }

  const hasVoids = paragraph.trailingVoids > 0 || paragraph.runs.some(run => run.voidsBefore > 0);
  if (paragraphText.length !== original.length) {
    const [run] = paragraph.runs;
    if (hasVoids || paragraph.runs.length !== 1 || !run)
      return { replacements: [], unaligned: hasVoids };
    return { replacements: [{ at: run.range, text: paragraphText }], unaligned: false };
  }

  let offset = 0;
  const replacements = paragraph.runs.flatMap(run => {
    offset += run.voidsBefore * CAPITALIZATION_VOID_SEPARATOR.length;
    const text = paragraphText.slice(offset, offset + run.text.length);
    offset += run.text.length;
    if (text === run.text) return [];
    return [{ at: run.range, text }];
  });
  return { replacements, unaligned: false };
}
